<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { BAlert, BButton, BModal } from 'bootstrap-vue-next';
import { useApolloClient } from '@vue/apollo-composable';
import { ComputeTasksDocument, ComputeTaskType } from '@/gql/graphql';
import { useUserStore } from '@/store/user';
import { computeTaskDebugSections } from '@/utils/compute-task-debug-sections';
import { highlightJson } from '@/utils/prism-utils';

const props = defineProps<{
  taskId: number;
  projectId: number | null;
}>();
const emit = defineEmits<{ (event: 'close'): void }>();
const { resolveClient } = useApolloClient();
const userStore = useUserStore();
const task = ref<ComputeTaskType | null>(null);
const debugError = ref(false);
const trigger = document.activeElement;
onUnmounted(() => {
  if (trigger instanceof HTMLElement && trigger.isConnected) trigger.focus();
});
const sections = computed(() => computeTaskDebugSections(task.value || {}));
const raw = ref(false);
const openSections = ref(new Set<string>());
onMounted(loadTask);
async function loadTask() {
  debugError.value = false;
  try {
    const { data } = await resolveClient().query({
      query: ComputeTasksDocument,
      variables: {
        id: props.taskId,
        projectId: props.projectId,
        acrossUsers: userStore.admin,
        pagingItemsPerPage: 1,
      },
      fetchPolicy: 'no-cache',
    });
    task.value = data.computeTasks?.computeTasks?.[0] || null;
    debugError.value = !task.value;
  } catch {
    debugError.value = true;
  }
}
function toggleSection(id: string, event: Event) {
  if ((event.target as HTMLDetailsElement).open) openSections.value.add(id);
  else openSections.value.delete(id);
}
</script>

<template>
  <BAlert v-if="debugError" :model-value="true" variant="danger">
    Could not load debug details. Please try again.
    <BButton size="sm" variant="outline-danger" @click="loadTask"
      >Retry</BButton
    >
  </BAlert>
  <BModal
    v-if="task"
    :model-value="true"
    size="xl"
    scrollable
    @hidden="emit('close')"
  >
    <template #header>
      <h2>
        <font-awesome-icon icon="code" class="me-2" />{{ task.name }} ({{
          task.id
        }})
      </h2>
    </template>
    <BButton
      class="mb-3"
      size="sm"
      variant="outline-secondary"
      :aria-pressed="raw"
      @click="raw = !raw"
      >Raw JSON</BButton
    >
    <pre v-if="raw" class="code-highlight" v-html="highlightJson(task)"></pre>
    <template v-else>
      <details
        v-for="section in sections"
        :key="section.id"
        :open="openSections.has(section.id)"
        class="mb-3"
        @toggle="toggleSection(section.id, $event)"
      >
        <summary>
          <h3 class="d-inline fs-6">{{ section.title }}</h3>
        </summary>
        <pre
          v-if="openSections.has(section.id)"
          class="code-highlight"
          v-html="highlightJson(section.content)"
        ></pre>
      </details>
    </template>
    <template #footer
      ><BButton variant="secondary" @click="emit('close')"
        >Close</BButton
      ></template
    >
  </BModal>
</template>
